import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import PrintShell, { PrintField, PrintRow, PrintSectionHeader, PrintTextBlock } from '../../../components/print/PrintShell';
import { toTitleCase, formatValue } from '../../../utils/formatText';

const isFilled = (value) => {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

const display = (value) => {
  if (!isFilled(value)) return '';
  if (Array.isArray(value)) return value.map(formatValue).join(', ');
  return formatValue(value);
};

const yes = (value) => value === true || String(value).toLowerCase() === 'yes' || String(value).toLowerCase() === 'true';

function CompactRow({ fields }) {
  const present = fields.filter((field) => isFilled(field.value));
  if (!present.length) return null;
  return (
    <PrintRow
      compact
      fields={present.map((field) => ({
        ...field,
        value: display(field.value),
      }))}
    />
  );
}

CompactRow.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any,
      flex: PropTypes.number,
    })
  ).isRequired,
};

function translateLookup(t, value) {
  if (!isFilled(value)) return '';
  const key = String(value).toLowerCase();
  if (t(key) !== key) return t(key);
  return toTitleCase(value);
}

export default function SacramentPrintTemplate({ record: r }) {
  const { t } = useTranslation();
  if (!r) return null;
  const fd = r.form_data || {};
  const od = r.office_data || {};

  const usedKeys = new Set([
    'child_name', 'sex', 'age', 'apt', 'grade', 'school', 'birth_place', 'birth_date',
    'address_line_1', 'city', 'state', 'zip', 'phone',
    'child_language', 'parent_language', 'lives_with', 'lives_with_other', 'can_go_home_alone',
    'mother_name', 'mother_phone', 'mother_email', 'mother_catholic',
    'father_name', 'father_phone', 'father_email', 'father_catholic',
    'guardian_name', 'guardian_phone', 'guardian_email', 'guardian_catholic',
    'parents_separated', 'other_parent_legal_access', 'has_court_order', 'court_order_details',
    'has_medical_issues', 'medical_issues_details', 'takes_medication', 'medication_details',
    'has_epipen', 'epipen_details', 'has_allergies', 'allergy_details',
    'has_learning_disability', 'learning_disability_details',
    'enrolled_last_year', 'prev_parish_education', 'prev_year', 'prev_parish_name',
    'has_baptism', 'has_communion', 'has_confirmation',
    'signature', 'signature_date',
    'emergency1_name', 'emergency1_relation', 'emergency1_phone',
    'emergency2_name', 'emergency2_relation', 'emergency2_phone',
    'emergency_auth', 'emergency_signature', 'emergency_signature_date',
    'photo_consent', 'photo_signature', 'photo_signature_date', 'photo_consent_name',
    'comments',
    'sponsor_name', 'sponsor_phone', 'sponsor_email', 'sponsor_religion',
  ]);

  const leftover = Object.entries(fd).filter(([key, val]) => !usedKeys.has(key) && isFilled(val));
  const leftoverRows = [];
  for (let i = 0; i < leftover.length; i += 3) {
    leftoverRows.push(leftover.slice(i, i + 3));
  }

  const received = [
    yes(fd.has_baptism) && t('print_sacrament_baptism'),
    yes(fd.has_communion) && t('print_sacrament_communion'),
    yes(fd.has_confirmation) && t('print_sacrament_confirmation'),
  ].filter(Boolean);

  return (
    <PrintShell printClass="sacrament-print-only" formTitle={t('print_sacrament_form_title')}>
      <PrintSectionHeader title={t('print_section_registration_data')} compact />
      <PrintRow compact fields={[
        { label: t('print_label_name'), value: r.full_name },
        { label: t('print_label_phone'), value: r.phone, flex: 0.7 },
      ]} />
      <PrintRow compact fields={[
        { label: t('print_label_email'), value: r.email },
        { label: t('print_label_dob'), value: r.birth_date, flex: 0.7 },
      ]} />
      <PrintField compact label={t('print_label_address')} value={r.full_address} />
      <PrintRow compact fields={[
        { label: t('print_label_language'), value: formatValue(r.language), flex: 0.6 },
        { label: t('print_label_program_year'), value: r.program_year, flex: 0.6 },
        { label: t('print_label_donation'), value: (r.donation_made === true || r.donation_made === 1 || r.donation_made === '1') ? t('Yes') : t('No'), flex: 0.45 },
      ]} />
      <PrintRow compact fields={[
        { label: t('print_label_sacrament'), value: translateLookup(t, r.sacrament_type) },
        { label: t('print_label_type'), value: translateLookup(t, r.form_type), flex: 0.5 },
        { label: t('print_label_status'), value: translateLookup(t, r.status), flex: 0.5 },
      ]} />

      <PrintSectionHeader title={t('print_section_child')} compact />
      <CompactRow fields={[
        { label: t('print_label_name'), value: fd.child_name },
        { label: t('print_label_sex'), value: fd.sex, flex: 0.4 },
        { label: t('print_label_age'), value: fd.age, flex: 0.35 },
      ]} />
      <CompactRow fields={[
        { label: t('print_label_address'), value: fd.address_line_1 },
        { label: t('print_label_apt'), value: fd.apt, flex: 0.35 },
        { label: t('print_label_city'), value: fd.city, flex: 0.55 },
      ]} />
      <CompactRow fields={[
        { label: t('print_label_state'), value: fd.state, flex: 0.4 },
        { label: t('print_label_zip'), value: fd.zip, flex: 0.45 },
        { label: t('print_label_phone'), value: fd.phone },
      ]} />
      <CompactRow fields={[
        { label: t('print_label_dob'), value: fd.birth_date },
        { label: t('print_label_birth_place'), value: fd.birth_place },
      ]} />
      <CompactRow fields={[
        { label: t('print_label_school'), value: fd.school },
        { label: t('print_label_grade'), value: fd.grade, flex: 0.45 },
      ]} />
      <CompactRow fields={[
        { label: t('print_label_child_language'), value: fd.child_language },
        { label: t('print_label_parent_language'), value: fd.parent_language },
        { label: t('print_label_lives_with'), value: fd.lives_with },
      ]} />
      <CompactRow fields={[
        { label: t('print_label_lives_with_other'), value: fd.lives_with_other },
        { label: t('print_label_go_home_alone'), value: fd.can_go_home_alone },
      ]} />

      <PrintSectionHeader title={t('print_section_parents_guardian')} compact />
      <CompactRow fields={[
        { label: t('print_label_mother'), value: fd.mother_name },
        { label: t('print_label_phone'), value: fd.mother_phone, flex: 0.7 },
        { label: t('print_label_catholic'), value: fd.mother_catholic, flex: 0.45 },
      ]} />
      <CompactRow fields={[{ label: t('print_label_mother_email'), value: fd.mother_email }]} />
      <CompactRow fields={[
        { label: t('print_label_father'), value: fd.father_name },
        { label: t('print_label_phone'), value: fd.father_phone, flex: 0.7 },
        { label: t('print_label_catholic'), value: fd.father_catholic, flex: 0.45 },
      ]} />
      <CompactRow fields={[{ label: t('print_label_father_email'), value: fd.father_email }]} />
      <CompactRow fields={[
        { label: t('print_label_guardian'), value: fd.guardian_name },
        { label: t('print_label_phone'), value: fd.guardian_phone, flex: 0.7 },
        { label: t('print_label_catholic'), value: fd.guardian_catholic, flex: 0.45 },
      ]} />
      <CompactRow fields={[{ label: t('print_label_guardian_email'), value: fd.guardian_email }]} />
      <CompactRow fields={[
        { label: t('print_label_parents_separated'), value: fd.parents_separated },
        { label: t('print_label_other_parent_access'), value: fd.other_parent_legal_access },
        { label: t('print_label_court_order'), value: fd.has_court_order },
      ]} />
      {isFilled(fd.court_order_details) && (
        <PrintField compact label={t('print_label_court_order_details')} value={display(fd.court_order_details)} />
      )}

      <PrintSectionHeader title={t('print_section_medical')} compact />
      <CompactRow fields={[
        { label: t('print_label_medical_issues'), value: fd.has_medical_issues },
        { label: t('print_label_medication'), value: fd.takes_medication },
        { label: t('print_label_epipen'), value: fd.has_epipen },
      ]} />
      {isFilled(fd.medical_issues_details) && (
        <PrintField compact label={t('print_label_medical_details')} value={display(fd.medical_issues_details)} />
      )}
      {isFilled(fd.medication_details) && (
        <PrintField compact label={t('print_label_medication_details')} value={display(fd.medication_details)} />
      )}
      {isFilled(fd.epipen_details) && (
        <PrintField compact label={t('print_label_epipen_details')} value={display(fd.epipen_details)} />
      )}
      <CompactRow fields={[
        { label: t('print_label_allergies'), value: fd.has_allergies },
        { label: t('print_label_learning_disability'), value: fd.has_learning_disability },
      ]} />
      {isFilled(fd.allergy_details) && (
        <PrintField compact label={t('print_label_allergy_details')} value={display(fd.allergy_details)} />
      )}
      {isFilled(fd.learning_disability_details) && (
        <PrintField compact label={t('print_label_learning_disability')} value={display(fd.learning_disability_details)} />
      )}

      <PrintSectionHeader title={t('print_section_religious_edu')} compact />
      <CompactRow fields={[
        { label: t('print_label_enrolled_last_year'), value: fd.enrolled_last_year },
        { label: t('print_label_other_parish_edu'), value: fd.prev_parish_education },
      ]} />
      <CompactRow fields={[
        { label: t('print_label_year'), value: fd.prev_year, flex: 0.4 },
        { label: t('print_label_parish'), value: fd.prev_parish_name },
      ]} />
      {received.length > 0 && (
        <PrintField compact label={t('print_label_sacraments_received')} value={received.join(', ')} />
      )}
      {(isFilled(fd.sponsor_name) || isFilled(fd.sponsor_phone) || isFilled(fd.sponsor_email) || isFilled(fd.sponsor_religion)) && (
        <>
          <PrintSectionHeader title={t('print_section_confirmation_sponsor')} compact />
          <CompactRow fields={[
            { label: t('sponsor_name'), value: fd.sponsor_name },
            { label: t('print_label_religion'), value: fd.sponsor_religion, flex: 0.6 },
          ]} />
          <CompactRow fields={[
            { label: t('print_label_phone'), value: fd.sponsor_phone },
            { label: t('print_label_email'), value: fd.sponsor_email },
          ]} />
        </>
      )}
      <CompactRow fields={[
        { label: t('print_label_signature'), value: fd.signature },
        { label: t('print_label_date'), value: fd.signature_date, flex: 0.5 },
      ]} />

      <PrintSectionHeader title={t('print_section_emergency')} compact />
      <CompactRow fields={[
        { label: t('print_label_name'), value: fd.emergency1_name },
        { label: t('print_label_relation'), value: fd.emergency1_relation, flex: 0.55 },
        { label: t('print_label_phone'), value: fd.emergency1_phone, flex: 0.7 },
      ]} />
      <CompactRow fields={[
        { label: t('print_label_name'), value: fd.emergency2_name },
        { label: t('print_label_relation'), value: fd.emergency2_relation, flex: 0.55 },
        { label: t('print_label_phone'), value: fd.emergency2_phone, flex: 0.7 },
      ]} />
      <CompactRow fields={[
        { label: t('print_label_emergency_auth'), value: fd.emergency_auth },
      ]} />
      <CompactRow fields={[
        { label: t('print_label_signature'), value: fd.emergency_signature },
        { label: t('print_label_date'), value: fd.emergency_signature_date, flex: 0.5 },
      ]} />

      <PrintSectionHeader title={t('print_section_photo')} compact />
      <CompactRow fields={[
        { label: t('print_label_consent'), value: fd.photo_consent, flex: 0.45 },
        { label: t('print_label_name'), value: fd.photo_consent_name },
      ]} />
      <CompactRow fields={[
        { label: t('print_label_signature'), value: fd.photo_signature },
        { label: t('print_label_date'), value: fd.photo_signature_date, flex: 0.5 },
      ]} />

      {leftoverRows.length > 0 && (
        <>
          <PrintSectionHeader title={t('print_section_additional')} compact />
          {leftoverRows.map((row) => (
            <PrintRow
              key={row.map(([key]) => key).join('-')}
              compact
              fields={row.map(([key, val]) => ({
                label: toTitleCase(key),
                value: display(val),
              }))}
            />
          ))}
        </>
      )}

      <PrintSectionHeader title={t('print_section_office')} compact />
      <PrintRow compact fields={[
        { label: t('print_label_group_class'), value: od.group },
        { label: t('print_label_catechist'), value: od.catechist },
      ]} />
      <PrintField compact label={t('print_label_sacraments_prepare')} value={(od.sacraments_to_prepare || []).join(', ')} />
      <PrintField compact label={t('print_label_documents_file')} value={(od.documents_on_file || []).join(', ')} />
      {[1, 2].map((n) => (
        <PrintRow
          key={n}
          compact
          fields={[
            { label: t('print_label_payment_n_date', { n }), value: od[`payment${n}_date`] },
            { label: t('print_label_amount'), value: od[`payment${n}_amount`], flex: 0.5 },
            { label: t('print_label_balance'), value: od[`payment${n}_balance`], flex: 0.5 },
            { label: t('print_label_receipt'), value: od[`payment${n}_receipt`], flex: 0.5 },
            { label: t('print_label_initial'), value: od[`payment${n}_initial`], flex: 0.4 },
          ]}
        />
      ))}
      <PrintRow compact fields={[
        { label: t('print_label_payment_status'), value: od.payment_status, flex: 0.6 },
        { label: t('print_label_late_fee'), value: od.late_fee ? t('print_label_late_fee_yes') : t('No'), flex: 0.4 },
      ]} />
      {(od.office_comments || []).filter((comment) => comment?.body).map((comment) => (
        <PrintTextBlock
          key={comment.id}
          label={[t('office_comments'), comment.author_name].filter(Boolean).join(' — ')}
          value={comment.body}
        />
      ))}
      {isFilled(fd.comments) && <PrintTextBlock label={t('print_label_comments')} value={fd.comments} />}

      {r.requires_religious_education && (
        <>
          <PrintSectionHeader title={t('print_notes')} compact />
          <div style={{ fontSize: '11px', lineHeight: 1.45, marginTop: '4px' }}>
            <div style={{ marginBottom: '4px', whiteSpace: 'pre-line' }}>{t('print_fee_re_and_year_end_note')}</div>
            <div style={{ marginBottom: '4px' }}>{t('print_fee_nonrefundable')}</div>
            <div style={{ whiteSpace: 'pre-line' }}>{t('print_fee_re_and_year_end_note')}</div>
          </div>
        </>
      )}
    </PrintShell>
  );
}

SacramentPrintTemplate.propTypes = {
  record: PropTypes.shape({
    form_data: PropTypes.object,
    office_data: PropTypes.object,
    full_name: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
    birth_date: PropTypes.string,
    full_address: PropTypes.string,
    language: PropTypes.string,
    program_year: PropTypes.string,
    donation_made: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    requires_religious_education: PropTypes.bool,
    sacrament_type: PropTypes.string,
    form_type: PropTypes.string,
    status: PropTypes.string,
  }),
};
